import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { ROUTES } from 'routes'
import type { Permissions } from 'types'

/**
 * Any internal path is returnable, DETAIL PAGES INCLUDED (VRG-369, revised
 * 2026-08-23 on user direction: "it should return to whatever the previous page
 * was they entered from").
 *
 * The first version allowed only the two LIST routes, reasoning that you can
 * enter a demo's space from a detail page, purge that demo, and come back to a
 * record that no longer exists. True — but it paid for that rare case on EVERY
 * return: entering a company's space from its detail page dumped you on the
 * Companies list, losing both the record and any filters you had set. The common
 * case is the one to optimise; a purged record lands on that page's own
 * not-found, which is recoverable and, for the AE who just purged it, not
 * confusing.
 *
 * The query string is kept deliberately, so filters and pagination survive the
 * round trip — same reasoning as `useReferrerState` (§42), where returning to a
 * reset list is called out as the thing that makes back-navigation feel broken.
 *
 * What is still enforced is that the value is an INTERNAL, HOST-RELATIVE path.
 * `returnTo` is persisted, so it can be an attacker-controlled or simply stale
 * string from storage; without this, the Return button becomes an open redirect.
 * `//evil.com` and `https://evil.com` both fail the leading `/internal` test.
 */
const isReturnableInternalPath = (path: string): boolean =>
  /^\/internal(?:[/?#]|$)/.test(path)

/**
 * Where "Return to Internal" should go. Falls back to Companies for anything that
 * is not an internal path, including `null` (a session that predates this field,
 * or one restored from storage by an older build).
 */
export const resolveReturnTo = (returnTo: string | null | undefined): string =>
  returnTo && isReturnableInternalPath(returnTo) ? returnTo : ROUTES.INTERNAL_COMPANIES

export interface VeraSpaceState {
  /** The company ID the Vera staffer is currently operating inside. Null = not in space. */
  companyId: string | null
  /** Human-readable company name for the banner ("You're in Brightwave's space"). */
  companyName: string | null
  /**
   * The permissions the staffer has inside this company's space, resolved from
   * the company's vera_support grant (company_custom_roles / company_custom_role_permissions).
   * REPLACE semantics: these are the effective permissions when space is active,
   * regardless of the staffer's own role permissions.
   */
  permissions: Permissions[]
  /**
   * VRG-369: where the caller that entered space wants to land, and the router state
   * to carry there. Read by `InternalRoute`, NOT by anything that navigates directly.
   *
   * It exists because entering space from an internal page is a genuine race that the
   * caller cannot win on its own. `enter()` flips `inSpace`, which re-renders the
   * internal route the caller is still sitting on, and `InternalRoute` then redirects
   * — with `replace` — because in-space staff are not allowed on internal routes. That
   * redirect is created during a render that happens BEFORE the caller's own
   * `navigate()` commits, so it lands second and clobbers it. Observed directly:
   * `push /products` immediately followed by `replace /settings/general`.
   *
   * Rather than order the two calls just so and hope React keeps batching them that
   * way, both paths are made to agree on the destination: whichever navigation wins,
   * the staffer ends up in the same place. Settings remains the default for callers
   * that do not set this (the Companies and Company pages), which is the right landing
   * when the reason for entering is to look at the brand's configuration.
   *
   * Deliberately NOT persisted — it is a one-shot handoff for a single entry, and a
   * reload should land on the ordinary space home, not re-run someone's intent from
   * a previous session.
   */
  landing: { path: string; state?: unknown } | null
  /**
   * VRG-369: the internal page the staffer came FROM, and the symmetric partner of
   * `landing` — that one is where to go IN, this is where to come BACK. Read by
   * `VeraSpaceBanner`'s "Return to Internal", through `resolveReturnTo`.
   *
   * It exists because the banner always navigated to Companies, so entering a
   * demo's space from the Demos console and coming back dumped you on a different
   * list, with the row you were working on nowhere in sight.
   *
   * ## Why this IS persisted when `landing` deliberately is not
   *
   * They answer different questions and therefore have different lifetimes.
   * `landing` is a ONE-SHOT navigation intent consumed on the way in; replaying a
   * previous session's intent after a reload would be wrong, so it is dropped.
   * `returnTo` is a property of the space SESSION — the same session `companyId`,
   * `companyName` and `permissions` are persisted for. Drop it and the banner
   * silently degrades to Companies after any reload, which is precisely the
   * behaviour this field exists to remove.
   *
   * The reason persisting is SAFE here is the allowlist above: only known internal
   * list routes can ever be stored, so a restored value cannot point at a record
   * that has since been purged. `exit()` clears it with the rest of the session.
   */
  returnTo: string | null
  /**
   * VRG-677: the `landing` race, on the way OUT. Set by `exit({ to })` to where "Return
   * to Internal" is navigating, read by every guard that bounces internal staff off a
   * brand page (`SettingsRoute`, `HomePage` via `resolveHomeLanding`), and cleared by
   * `InternalRoute` once the staffer is on an internal page.
   *
   * Why it has to exist: `exit()` is a zustand update, which React renders
   * synchronously, while the banner's `navigate()` is a transition (`index.tsx` enables
   * `v7_startTransition`). So the first render after the click is the OLD page (say
   * `/settings/general`) with the space already gone. `SettingsRoute` sees an internal
   * user on a company settings URL and renders `<Navigate replace>` to the staffer's
   * Account page, which replaces the history entry the banner just pushed. The staffer
   * lands on `/settings/profile` instead of Companies. Same shape as `landing`, same fix:
   * make both navigations agree on the destination, so it does not matter which one wins.
   *
   * Not persisted: a one-shot handoff, exactly like `landing`.
   */
  leaving: string | null

  /** Activate space mode for the given company. */
  enter: (params: {
    companyId: string
    companyName: string
    permissions: Permissions[]
    landing?: { path: string; state?: unknown }
    /** Where "Return to Internal" goes. Anything off the allowlist becomes Companies. */
    returnTo?: string
  }) => void
  /**
   * Exit space mode and clear all space state. `to` is where the caller is about to
   * navigate (see `leaving`); omit it when nothing navigates (sign-in, sign-out).
   */
  exit: (params?: { to?: string }) => void
  /** Drop the `leaving` handoff once it has been honoured. */
  clearLeaving: () => void
}

export const useVeraSpaceStore = create<VeraSpaceState>()(
  persist(
    (set) => ({
      companyId: null,
      companyName: null,
      permissions: [],
      landing: null,
      returnTo: null,
      leaving: null,

      enter: ({ companyId, companyName, permissions, landing, returnTo }) =>
        set({
          companyId,
          companyName,
          permissions,
          landing: landing ?? null,
          // Normalised on the way IN as well as on the way out, so an unrecognised
          // path can never be written to storage in the first place.
          returnTo: resolveReturnTo(returnTo),
          leaving: null,
        }),

      exit: (params) =>
        set({
          companyId: null,
          companyName: null,
          permissions: [],
          landing: null,
          returnTo: null,
          // Through the same allowlist as `returnTo`, so a guard can only ever be told
          // to bounce to an internal path.
          leaving: params?.to ? resolveReturnTo(params.to) : null,
        }),

      clearLeaving: () => set({ leaving: null }),
    }),
    {
      name: 'veraSpace',
      // Only persist the data fields, not the action functions. `landing` and
      // `leaving` are excluded on purpose (one-shot intents); `returnTo` is included on purpose
      // (session property) — see its doc for why the two differ.
      partialize: (state) => ({
        companyId: state.companyId,
        companyName: state.companyName,
        permissions: state.permissions,
        returnTo: state.returnTo,
      }),
    },
  ),
)

/**
 * VRG-661 — end any persisted space session, from outside React.
 *
 * The space store is persisted to localStorage, but the access token it rides on is a
 * browser-session cookie (`utils/storage.ts` sets it with no expiry). So a browser restart
 * drops the token WITHOUT running any logout code, the space survives, and the next
 * sign-in resumed it: a platform_admin signing in landed on a brand's Settings with that
 * brand's (often empty) Vera-support grant. Every sign-in and sign-out path calls this so
 * a new session always starts as the staffer themselves.
 *
 * Deliberately does NOT post the exit-space audit (`useVeraSpaceMutationsQuery.exitAsync`).
 * That endpoint is a log line only (`company.internal.controller.ts` exitSpace), and these
 * call sites run when the old token is already gone or about to be replaced.
 *
 * No-op when not in space, so callers need not check first.
 */
export const endVeraSpaceSession = (): void => {
  if (useVeraSpaceStore.getState().companyId) useVeraSpaceStore.getState().exit()
}

/**
 * VRG-661 — keep every tab's space state in step with storage.
 *
 * The API client already reads the `veraSpace` entry straight from localStorage on every
 * request (`services/index.ts`), so the X-Vera-Space header has always followed storage
 * across tabs while the UI (banner, guards, `useCanAccess`) followed this tab's in-memory
 * copy. Ending a space in one tab (Return to Internal, sign-out, or now opening an
 * `/internal/*` URL) left other tabs rendering a brand space whose requests no longer
 * carried it. Rehydrating on the `storage` event, which fires only in OTHER tabs, makes
 * the UI agree with the headers.
 */
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (event) => {
    if (event.key === null || event.key === 'veraSpace') {
      void useVeraSpaceStore.persist.rehydrate()
    }
  })
}
